import {Dispatch, FC, createContext, useContext, useEffect, useRef, useState} from 'react'
import {AuthModel, UserModel} from '../../app/modules/auth/core/models'
import {CommonService} from '../../app/modules/common'
import {SwapperComponent} from '../assets/ts/components'
import * as authHelper from '../helpers'
import {useCancelToken} from '../hooks'
import {useLanguage} from '../i18n/Metronici18n'
import {LayoutSplashScreen} from '../layout'
import {PERMISSION} from '../constants'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: (currentUser: UserModel | undefined) => void
  logout: () => void
  handleSaveCurrentUser: (currentUser: UserModel | undefined) => void
  loadingSwitch: boolean
  setLoadingSwitch: Dispatch<boolean>
  isUserRole: boolean
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  handleSaveCurrentUser: () => {},
  loadingSwitch: false,
  setLoadingSwitch: () => {},
  isUserRole: false,
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC = ({children}) => {
  const {setLanguage} = useLanguage()
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>(undefined)
  const [loadingSwitch, setLoadingSwitch] = useState(false)

  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const handleSaveCurrentUser = (currentUser: UserModel | undefined) => {
    if (!currentUser) return
    setLanguage(currentUser?.language)
    setCurrentUser(currentUser)
  }

  const logout = async () => {
    await SwapperComponent.removeInstances() // fix title header
    await saveAuth(undefined)
    setLoadingSwitch(false)
    await setCurrentUser(undefined)
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        handleSaveCurrentUser,
        loadingSwitch,
        setLoadingSwitch,
        // This is VNP user
        isUserRole: currentUser?.is_user_role === 1,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC = ({children}) => {
  const {auth, logout, handleSaveCurrentUser, currentUser} = useAuth()
  const {newCancelToken, isCancel} = useCancelToken()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)

  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const config = {cancelToken: newCancelToken()}

    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const {data}: any = await CommonService.getUser(config)
          if (data) {
            handleSaveCurrentUser(data)
          }
        }
      } catch (error) {
        if (isCancel(error)) return
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth && auth.access_token) {
      requestUser()
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    document.documentElement.setAttribute(
      'data-bs-theme',
      currentUser?.role || PERMISSION.DESTINATION
    )
  }, [currentUser?.role])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export {AuthInit, AuthProvider, useAuth}
